import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  ArrowLeft, Bot, Building2, Diff, Download, Edit3, ExternalLink, FileSignature,
  GitBranch, Lock, Paperclip, Printer, Save, Sparkles, UploadCloud, X,
} from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField, FormGrid, FormSection } from '@/components/forms/FormSection';
import { apiClient } from '@/lib/apiClient';
import {
  downloadAgreementPdf,
  getAgreement,
  refineClause,
  updateAgreement,
  uploadCounterpartyVersion,
  type AgreementDetail,
  type AgreementStatus,
  type AgreementVersionSummary,
  type SignatureMethod,
  type SignerParty,
  type UpdateAgreementRequest,
} from '@/api/agreements';

/**
 * Full agreement detail + editor. Single-screen handles:
 *
 *  - Read-only display of header, parties, terms, clauses, versions, signatures
 *  - Edit mode for the agreement record (scalar fields)
 *  - Edit mode for the CURRENT version's field_values (parties + clause overrides)
 *  - "Nueva versión Lopettia" — clones current values into a fresh version
 *  - "Subir versión de la contraparte" — registers a counterparty-revised version
 *  - "Registrar firma" — manual signature row for the current version
 *  - "Diff vs anterior" — modal with side-by-side text diff of two versions
 *  - "Export PDF" — opens print view (browser saves as PDF)
 *  - "Export Word" — downloads a .doc HTML blob (Word opens it)
 *
 * The agreement record is locked (read-only) once status = SIGNED.
 */

const STATUS_LABEL_KEY = (s: string) => `agreements.status.${s}`;
const KIND_LABEL_KEY = (k: string) => `agreements.kind.${k}`;
const ORIGIN_LABEL_KEY = (o: string) => `agreements.version.origin.${o}`;
const METHOD_LABEL_KEY = (m: string) => `agreements.signature.method.${m}`;
const PARTY_LABEL_KEY = (p: string) => `agreements.signature.party.${p}`;

const EDITABLE_STATUSES: AgreementStatus[] = ['DRAFT', 'IN_REVIEW', 'EXPIRED', 'TERMINATED', 'SUPERSEDED'];
const ALL_STATUSES: AgreementStatus[] = ['DRAFT', 'IN_REVIEW', 'SIGNED', 'EXPIRED', 'TERMINATED', 'SUPERSEDED'];
const ALL_METHODS: SignatureMethod[] = ['DOCUSIGN', 'PANDADOC', 'WET_INK_SCANNED', 'DIGITAL_LOPETTIA', 'EXTERNAL'];
const ALL_PARTIES: SignerParty[] = ['LOPETTIA', 'COUNTERPARTY'];

interface GovernanceContact { name?: string; title?: string; email?: string }
interface PartyValue {
  name?: string;
  short_name?: string;
  legal_form?: string;
  address?: string;
  representative?: string;
  representative_title?: string;
  governance_contact?: GovernanceContact;
}
interface ClauseOverride { override?: boolean; body?: string }
interface FieldValuesShape {
  title?: string;
  parties?: PartyValue[];
  governing_law?: string;
  arbitration_seat?: string;
  arbitration_language?: string;
  signing_date_day?: string;
  signing_date_month?: string;
  signing_date_year?: string;
  initial_term_years?: number;
  non_circumvent_months?: number;
  confidentiality_survival_years?: number;
  clauses?: Record<string, ClauseOverride>;
  [k: string]: unknown;
}

export function AgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [agreement, setAgreement] = useState<AgreementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ─── Header edit (scalar fields) ───
  const [editMode, setEditMode] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftStatus, setDraftStatus] = useState<AgreementStatus>('DRAFT');
  const [draftCounterparty, setDraftCounterparty] = useState('');
  const [draftEffectiveDate, setDraftEffectiveDate] = useState('');
  const [draftExpirationDate, setDraftExpirationDate] = useState('');
  const [draftSignedDate, setDraftSignedDate] = useState('');
  const [draftGoverningLaw, setDraftGoverningLaw] = useState('');
  const [draftJurisdiction, setDraftJurisdiction] = useState('');
  const [draftTags, setDraftTags] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  // ─── Parties + clauses editor (acts on current version's field_values) ───
  const [partiesEditMode, setPartiesEditMode] = useState(false);
  const [partiesDraft, setPartiesDraft] = useState<PartyValue[]>([]);
  const [savingParties, setSavingParties] = useState(false);

  const [clausesEditMode, setClausesEditMode] = useState(false);
  const [clausesDraft, setClausesDraft] = useState<Record<string, string>>({});
  const [savingClauses, setSavingClauses] = useState(false);

  // ─── Signature modal ───
  const [sigOpen, setSigOpen] = useState(false);
  const [savingSig, setSavingSig] = useState(false);
  const [sigForm, setSigForm] = useState({
    signerParty: 'COUNTERPARTY' as SignerParty,
    signerName: '',
    signerTitle: '',
    signerEmail: '',
    method: 'EXTERNAL' as SignatureMethod,
    signedAt: '',
  });

  // ─── New Lopettia version ───
  const [newVerOpen, setNewVerOpen] = useState(false);
  const [savingNewVer, setSavingNewVer] = useState(false);
  const [newVerNotes, setNewVerNotes] = useState('');

  // ─── Upload counterparty version ───
  const [upCpOpen, setUpCpOpen] = useState(false);
  const [savingUpCp, setSavingUpCp] = useState(false);
  const [upCpFile, setUpCpFile] = useState<File | null>(null);
  const [upCpNotes, setUpCpNotes] = useState('');

  // ─── AI clause assist ───
  const [aiOpen, setAiOpen] = useState(false);
  const [aiClauseNum, setAiClauseNum] = useState('');
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [aiModel, setAiModel] = useState('');

  // ─── Diff modal ───
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffFromId, setDiffFromId] = useState<string>('');
  const [diffToId, setDiffToId] = useState<string>('');

  const isSigned = agreement?.status === 'SIGNED';
  const canEdit = useMemo(() => agreement != null && !isSigned, [agreement, isSigned]);
  const currentVersion: AgreementVersionSummary | undefined = agreement?.versions.find(
    (v) => v.id === agreement?.currentVersionId,
  ) ?? agreement?.versions[0];
  const fv: FieldValuesShape = (currentVersion?.fieldValues as FieldValuesShape) ?? {};

  function notify(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function loadAgreement(opts: { quiet?: boolean } = {}) {
    if (!id) return Promise.resolve();
    if (!opts.quiet) setLoading(true);
    setError(null);
    return getAgreement(id)
      .then(setAgreement)
      .catch((err) => {
        if (isAxiosError(err) && err.response?.status === 404) {
          setError(t('agreements.detail.notFound'));
        } else {
          setError(err?.message ?? t('common.loadError', { defaultValue: 'Failed to load' }));
        }
      })
      .finally(() => { if (!opts.quiet) setLoading(false); });
  }

  useEffect(() => {
    let cancelled = false;
    if (!id) return;
    setLoading(true); setError(null);
    getAgreement(id)
      .then((d) => { if (!cancelled) setAgreement(d); })
      .catch((err) => {
        if (cancelled) return;
        if (isAxiosError(err) && err.response?.status === 404) setError(t('agreements.detail.notFound'));
        else setError(err?.message ?? t('common.loadError', { defaultValue: 'Failed to load' }));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, t]);

  // ─── Edit handlers ────────────────────────────────────────────────

  function openEdit() {
    if (!agreement) return;
    setDraftTitle(agreement.title);
    setDraftStatus(agreement.status as AgreementStatus);
    setDraftCounterparty(agreement.counterpartyDisplayName);
    setDraftEffectiveDate(agreement.effectiveDate ?? '');
    setDraftExpirationDate(agreement.expirationDate ?? '');
    setDraftSignedDate(agreement.signedDate ?? '');
    setDraftGoverningLaw(agreement.governingLaw ?? '');
    setDraftJurisdiction(agreement.governingJurisdiction ?? '');
    setDraftTags(agreement.tags.join(', '));
    setDraftNotes(agreement.notes ?? '');
    setEditError(null);
    setEditMode(true);
  }
  function cancelEdit() { setEditMode(false); setEditError(null); }
  async function saveEdit(e: FormEvent) {
    e.preventDefault();
    if (!agreement) return;
    setSavingEdit(true); setEditError(null);
    try {
      const tags = draftTags.split(',').map((s) => s.trim()).filter(Boolean);
      const body: UpdateAgreementRequest = {
        title: draftTitle.trim(),
        status: draftStatus,
        counterpartyDisplayName: draftCounterparty.trim(),
        effectiveDate: draftEffectiveDate || undefined,
        expirationDate: draftExpirationDate || undefined,
        signedDate: draftSignedDate || undefined,
        governingLaw: draftGoverningLaw.trim() || undefined,
        governingJurisdiction: draftJurisdiction.trim() || undefined,
        tags,
        notes: draftNotes,
      };
      await updateAgreement(agreement.id, body);
      await loadAgreement({ quiet: true });
      setEditMode(false);
      notify(t('agreements.toast.saved'));
    } catch (err) {
      const detail = isAxiosError(err) ? (err.response?.data as { detail?: string } | undefined)?.detail : null;
      setEditError(detail ?? (err as Error)?.message ?? t('common.loadError'));
    } finally { setSavingEdit(false); }
  }

  // ─── Parties editor ───────────────────────────────────────────────

  function openPartiesEdit() {
    const start = (fv.parties && fv.parties.length > 0)
      ? structuredClone(fv.parties)
      : [{ short_name: 'LOPETTIA' } as PartyValue, { short_name: 'CONTRAPARTE' } as PartyValue];
    setPartiesDraft(start);
    setPartiesEditMode(true);
  }
  function updateParty(i: number, key: keyof PartyValue, value: string) {
    setPartiesDraft((prev) => prev.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));
  }
  function updateGovernance(i: number, key: keyof GovernanceContact, value: string) {
    setPartiesDraft((prev) => prev.map((p, idx) => idx === i
      ? { ...p, governance_contact: { ...(p.governance_contact ?? {}), [key]: value } }
      : p));
  }
  function addParty() {
    setPartiesDraft((prev) => [...prev, { short_name: `PARTE_${prev.length + 1}` }]);
  }
  function removeParty(i: number) {
    setPartiesDraft((prev) => prev.filter((_, idx) => idx !== i));
  }
  async function saveParties() {
    if (!agreement) return;
    setSavingParties(true);
    try {
      const nextFv = { ...fv, parties: partiesDraft };
      await updateAgreement(agreement.id, { fieldValuesJson: JSON.stringify(nextFv) });
      await loadAgreement({ quiet: true });
      setPartiesEditMode(false);
      notify(t('agreements.toast.partiesSaved'));
    } catch {
      notify(t('agreements.toast.saveFailed'));
    } finally { setSavingParties(false); }
  }

  // ─── Clauses editor ───────────────────────────────────────────────

  function openClausesEdit() {
    const draft: Record<string, string> = {};
    for (const [k, v] of Object.entries(fv.clauses ?? {})) {
      if (v?.body) draft[k] = v.body;
    }
    setClausesDraft(draft);
    setClausesEditMode(true);
  }
  function updateClause(num: string, body: string) {
    setClausesDraft((prev) => ({ ...prev, [num]: body }));
  }
  function resetClause(num: string) {
    setClausesDraft((prev) => {
      const next = { ...prev };
      delete next[num];
      return next;
    });
  }
  async function saveClauses() {
    if (!agreement) return;
    setSavingClauses(true);
    try {
      const overrides: Record<string, ClauseOverride> = {};
      for (const [num, body] of Object.entries(clausesDraft)) {
        if (body && body.trim().length > 0) {
          overrides[num] = { override: true, body };
        }
      }
      const nextFv = { ...fv, clauses: overrides };
      await updateAgreement(agreement.id, { fieldValuesJson: JSON.stringify(nextFv) });
      await loadAgreement({ quiet: true });
      setClausesEditMode(false);
      notify(t('agreements.toast.clausesSaved'));
    } catch {
      notify(t('agreements.toast.saveFailed'));
    } finally { setSavingClauses(false); }
  }

  // ─── Signature modal ──────────────────────────────────────────────

  async function submitSignature(e: FormEvent) {
    e.preventDefault();
    if (!agreement) return;
    setSavingSig(true);
    try {
      const signedAtIso = sigForm.signedAt
        ? new Date(sigForm.signedAt).toISOString()
        : new Date().toISOString();
      await apiClient.post(`/agreements/${agreement.id}/signatures`, {
        ...sigForm,
        signedAt: signedAtIso,
      });
      await loadAgreement({ quiet: true });
      setSigOpen(false);
      setSigForm({ signerParty: 'COUNTERPARTY', signerName: '', signerTitle: '', signerEmail: '', method: 'EXTERNAL', signedAt: '' });
      notify(t('agreements.toast.signatureAdded'));
    } catch {
      notify(t('agreements.toast.saveFailed'));
    } finally { setSavingSig(false); }
  }

  // ─── New Lopettia version ─────────────────────────────────────────

  async function submitNewVersion() {
    if (!agreement) return;
    setSavingNewVer(true);
    try {
      await apiClient.post(`/agreements/${agreement.id}/versions`, {
        origin: 'LOPETTIA_EMITTED',
        fieldValuesJson: JSON.stringify(fv),
        diffAgainstVersionId: currentVersion?.id,
        notes: newVerNotes || 'Nueva versión emitida por Lopettia (clone de v actual).',
      });
      await loadAgreement({ quiet: true });
      setNewVerOpen(false);
      setNewVerNotes('');
      notify(t('agreements.toast.versionAdded'));
    } catch {
      notify(t('agreements.toast.saveFailed'));
    } finally { setSavingNewVer(false); }
  }

  // ─── Upload counterparty version ──────────────────────────────────

  async function submitUploadCp() {
    if (!agreement) return;
    if (!upCpFile) { notify(t('agreements.uploadCp.fileRequired')); return; }
    setSavingUpCp(true);
    try {
      await uploadCounterpartyVersion(agreement.id, upCpFile, upCpNotes || undefined);
      await loadAgreement({ quiet: true });
      setUpCpOpen(false);
      setUpCpFile(null); setUpCpNotes('');
      notify(t('agreements.toast.versionAdded'));
    } catch {
      notify(t('agreements.toast.saveFailed'));
    } finally { setSavingUpCp(false); }
  }

  // ─── AI clause refine ─────────────────────────────────────────────

  function openAi(clauseNum: string) {
    setAiClauseNum(clauseNum);
    setAiInstruction('');
    setAiResult('');
    setAiModel('');
    setAiOpen(true);
  }
  async function runAi() {
    if (!agreement) return;
    if (!aiInstruction.trim()) { notify(t('agreements.ai.instructionRequired')); return; }
    setAiLoading(true);
    try {
      const current = clausesDraft[aiClauseNum] ?? '';
      const out = await refineClause(agreement.id, {
        clauseNumber: aiClauseNum,
        currentText: current,
        instruction: aiInstruction,
      });
      setAiResult(out.refinedText);
      setAiModel(out.model);
    } catch {
      notify(t('agreements.ai.failed'));
    } finally { setAiLoading(false); }
  }
  function applyAi() {
    if (!aiResult) return;
    setClausesDraft((prev) => ({ ...prev, [aiClauseNum]: aiResult }));
    setAiOpen(false);
  }

  // ─── Diff ─────────────────────────────────────────────────────────

  function openDiff() {
    if (!agreement) return;
    const vs = agreement.versions;
    if (vs.length < 2) {
      notify(t('agreements.toast.diffNeedsTwo'));
      return;
    }
    setDiffFromId(vs[1].id);  // previous
    setDiffToId(vs[0].id);     // current (newest)
    setDiffOpen(true);
  }

  // ─── Export PDF / Word ────────────────────────────────────────────

  async function exportPdf() {
    if (!agreement) return;
    try {
      const blob = await downloadAgreementPdf(agreement.id, currentVersion?.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${agreement.title.replace(/[^a-z0-9_-]+/gi, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      notify(t('agreements.toast.exportFailed'));
    }
  }
  function exportWord() {
    if (!agreement) return;
    const html = renderAgreementHtml(agreement, currentVersion ?? null, fv);
    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${agreement.title.replace(/[^a-z0-9_-]+/gi, '_')}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ─── Render ───────────────────────────────────────────────────────

  if (loading) return <div className="p-8 text-sm text-muted-foreground">{t('common.loading')}</div>;
  if (error || !agreement) {
    return (
      <div className="space-y-3 p-6">
        <Button size="sm" variant="outline" onClick={() => navigate('/agreements')}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('agreements.detail.back')}
        </Button>
        <div className="rounded-lg border border-red-800 bg-red-950/40 p-4 text-sm text-red-300">{error}</div>
      </div>
    );
  }

  const partyA = fv.parties?.[0];
  const partyB = fv.parties?.[1];
  const extraParties = (fv.parties ?? []).slice(2);

  return (
    <div className="space-y-4">
      <Breadcrumbs
        crumbs={[
          { label: t('agreements.nav.title'), to: '/agreements' },
          { label: agreement.title },
        ]}
      />

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 rounded-md border bg-card px-4 py-2 text-sm shadow-lg">{toast}</div>
      )}

      {/* Header */}
      <div className="rounded-lg border bg-card p-6 print:border-0 print:p-0">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 print:hidden">
              <span className="rounded-full bg-indigo-900/40 px-2 py-0.5 text-[10px] font-medium text-indigo-200">
                {t(KIND_LABEL_KEY(agreement.kind))}
              </span>
              <span className="rounded-full bg-emerald-900/40 px-2 py-0.5 text-[10px] font-medium text-emerald-200">
                {t(STATUS_LABEL_KEY(agreement.status))}
              </span>
              {agreement.signedDate && (
                <span className="text-xs text-muted-foreground">
                  {t('agreements.column.signed')}: {agreement.signedDate}
                </span>
              )}
            </div>
            <h1 className="mt-1 text-2xl font-semibold flex items-center gap-2">
              <FileSignature className="h-6 w-6 print:hidden" />
              {agreement.title}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {agreement.counterpartyDisplayName}
              {agreement.accountId && agreement.accountName && (
                <>
                  {' · '}
                  <Link to={`/accounts/${agreement.accountId}`} className="inline-flex items-center gap-1 text-primary hover:underline print:text-foreground">
                    <Building2 className="h-3 w-3" />
                    {agreement.accountName}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            {canEdit && !editMode && (
              <Button size="sm" variant="outline" onClick={openEdit}>
                <Edit3 className="mr-1 h-3.5 w-3.5" />{t('agreements.action.edit')}
              </Button>
            )}
            {isSigned && (
              <Button size="sm" variant="outline" disabled title={t('agreements.action.signedNotEditable')}>
                <Lock className="mr-1 h-3.5 w-3.5" />{t('agreements.action.signedNotEditable')}
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setNewVerOpen(true)}>
                <GitBranch className="mr-1 h-3.5 w-3.5" />{t('agreements.action.newVersion')}
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setUpCpOpen(true)}>
                <UploadCloud className="mr-1 h-3.5 w-3.5" />{t('agreements.action.uploadCounterparty')}
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setSigOpen(true)}>
                <FileSignature className="mr-1 h-3.5 w-3.5" />{t('agreements.action.registerSignature')}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={openDiff} disabled={(agreement.versions.length ?? 0) < 2}>
              <Diff className="mr-1 h-3.5 w-3.5" />{t('agreements.action.diff')}
            </Button>
            <Button size="sm" variant="outline" onClick={exportPdf}>
              <Printer className="mr-1 h-3.5 w-3.5" />{t('agreements.action.exportPdf')}
            </Button>
            <Button size="sm" variant="outline" onClick={exportWord}>
              <Download className="mr-1 h-3.5 w-3.5" />{t('agreements.action.exportDocx')}
            </Button>
          </div>
        </div>
      </div>

      {/* Header edit form */}
      {editMode && (
        <form onSubmit={saveEdit} className="rounded-lg border border-primary/40 bg-card p-6 space-y-3 print:hidden">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Edit3 className="h-4 w-4 text-primary" />{t('agreements.edit.title')}
            </h2>
          </div>
          <FormGrid>
            <FormField label={t('agreements.edit.field.title')} required>
              <Input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} required />
            </FormField>
            <FormField label={t('agreements.edit.field.status')}>
              <select
                value={draftStatus}
                onChange={(e) => setDraftStatus(e.target.value as AgreementStatus)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {ALL_STATUSES.map((s) => {
                  const isAllowed = EDITABLE_STATUSES.includes(s) || s === 'SIGNED';
                  return <option key={s} value={s} disabled={!isAllowed}>{t(STATUS_LABEL_KEY(s))}</option>;
                })}
              </select>
            </FormField>
          </FormGrid>
          <FormField label={t('agreements.edit.field.counterparty')} required>
            <Input value={draftCounterparty} onChange={(e) => setDraftCounterparty(e.target.value)} required />
          </FormField>
          <FormGrid cols={3}>
            <FormField label={t('agreements.edit.field.effectiveDate')}>
              <Input type="date" value={draftEffectiveDate} onChange={(e) => setDraftEffectiveDate(e.target.value)} />
            </FormField>
            <FormField label={t('agreements.edit.field.expirationDate')}>
              <Input type="date" value={draftExpirationDate} onChange={(e) => setDraftExpirationDate(e.target.value)} />
            </FormField>
            <FormField label={t('agreements.edit.field.signedDate')} hint={t('agreements.edit.field.signedDateHint')}>
              <Input type="date" value={draftSignedDate} onChange={(e) => setDraftSignedDate(e.target.value)} />
            </FormField>
          </FormGrid>
          <FormGrid>
            <FormField label={t('agreements.edit.field.governingLaw')}>
              <Input value={draftGoverningLaw} onChange={(e) => setDraftGoverningLaw(e.target.value)} />
            </FormField>
            <FormField label={t('agreements.edit.field.jurisdiction')}>
              <Input value={draftJurisdiction} onChange={(e) => setDraftJurisdiction(e.target.value)} />
            </FormField>
          </FormGrid>
          <FormField label={t('agreements.edit.field.tags')} hint={t('agreements.edit.field.tagsHint')}>
            <Input value={draftTags} onChange={(e) => setDraftTags(e.target.value)} placeholder="prospecto, partnership, q3-2026" />
          </FormField>
          <FormField label={t('agreements.edit.field.notes')}>
            <textarea value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} rows={4} className="w-full rounded-md border bg-background px-3 py-2 text-sm" />
          </FormField>
          {editError && <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">{editError}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={cancelEdit} disabled={savingEdit}>
              <X className="mr-1 h-3.5 w-3.5" />{t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={savingEdit || !draftTitle.trim() || !draftCounterparty.trim()}>
              <Save className="mr-1 h-3.5 w-3.5" />{savingEdit ? t('agreements.edit.saving') : t('agreements.edit.save')}
            </Button>
          </div>
        </form>
      )}

      {/* Parties */}
      <FormSection title={t('agreements.section.parties')} defaultOpen
        subtitle={canEdit && !partiesEditMode ? (
          <button type="button" onClick={openPartiesEdit} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            <Edit3 className="h-3 w-3" />{t('agreements.action.edit')}
          </button>
        ) as unknown as string : undefined}>
        {partiesEditMode ? (
          <div className="space-y-3">
            {partiesDraft.map((p, i) => (
              <div key={i} className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase text-muted-foreground">
                    {i === 0 ? t('agreements.party.lopettia') : i === 1 ? t('agreements.party.counterparty') : `${t('agreements.party.additional')} ${i + 1}`}
                  </div>
                  {i >= 2 && (
                    <button type="button" onClick={() => removeParty(i)} className="text-xs text-red-400 hover:underline">
                      <X className="inline h-3 w-3" /> {t('common.remove')}
                    </button>
                  )}
                </div>
                <FormGrid>
                  <FormField label={t('agreements.party.field.name')}><Input value={p.name ?? ''} onChange={(e) => updateParty(i, 'name', e.target.value)} /></FormField>
                  <FormField label={t('agreements.party.field.shortName')}><Input value={p.short_name ?? ''} onChange={(e) => updateParty(i, 'short_name', e.target.value)} /></FormField>
                </FormGrid>
                <FormField label={t('agreements.party.field.legalForm')}><Input value={p.legal_form ?? ''} onChange={(e) => updateParty(i, 'legal_form', e.target.value)} /></FormField>
                <FormField label={t('agreements.party.field.address')}><Input value={p.address ?? ''} onChange={(e) => updateParty(i, 'address', e.target.value)} /></FormField>
                <FormGrid>
                  <FormField label={t('agreements.party.field.representative')}><Input value={p.representative ?? ''} onChange={(e) => updateParty(i, 'representative', e.target.value)} /></FormField>
                  <FormField label={t('agreements.party.field.representativeTitle')}><Input value={p.representative_title ?? ''} onChange={(e) => updateParty(i, 'representative_title', e.target.value)} /></FormField>
                </FormGrid>
                <div className="border-t pt-2">
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">{t('agreements.party.governance')}</div>
                  <FormGrid cols={3}>
                    <FormField label={t('agreements.party.field.govName')}><Input value={p.governance_contact?.name ?? ''} onChange={(e) => updateGovernance(i, 'name', e.target.value)} /></FormField>
                    <FormField label={t('agreements.party.field.govTitle')}><Input value={p.governance_contact?.title ?? ''} onChange={(e) => updateGovernance(i, 'title', e.target.value)} /></FormField>
                    <FormField label={t('agreements.party.field.govEmail')}><Input value={p.governance_contact?.email ?? ''} onChange={(e) => updateGovernance(i, 'email', e.target.value)} /></FormField>
                  </FormGrid>
                </div>
              </div>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button type="button" size="sm" variant="outline" onClick={addParty}>+ {t('agreements.party.add')}</Button>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setPartiesEditMode(false)} disabled={savingParties}>{t('common.cancel')}</Button>
                <Button type="button" size="sm" onClick={saveParties} disabled={savingParties}>
                  {savingParties ? t('agreements.edit.saving') : t('agreements.edit.save')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <FormGrid>
              <PartyBlock label={t('agreements.party.lopettia')} value={partyA} />
              <PartyBlock label={t('agreements.party.counterparty')} value={partyB} />
            </FormGrid>
            {extraParties.length > 0 && (
              <FormGrid>
                {extraParties.map((p, i) => <PartyBlock key={i} label={`${t('agreements.party.additional')} ${i + 3}`} value={p} />)}
              </FormGrid>
            )}
          </>
        )}
      </FormSection>

      {/* Key terms */}
      <FormSection title={t('agreements.section.terms')} defaultOpen>
        <FormGrid cols={3}>
          <ReadField label={t('agreements.field.initialTermYears')} value={fv.initial_term_years != null ? String(fv.initial_term_years) : null} />
          <ReadField label={t('agreements.field.nonCircumventMonths')} value={fv.non_circumvent_months != null ? String(fv.non_circumvent_months) : null} />
          <ReadField label={t('agreements.field.confidentialityYears')} value={fv.confidentiality_survival_years != null ? String(fv.confidentiality_survival_years) : null} />
          <ReadField label={t('agreements.field.governingLaw')} value={fv.governing_law ?? agreement.governingLaw} />
          <ReadField label={t('agreements.field.arbitrationSeat')} value={fv.arbitration_seat ?? agreement.governingJurisdiction} />
          <ReadField label={t('agreements.field.arbitrationLanguage')} value={fv.arbitration_language ?? null} />
        </FormGrid>
      </FormSection>

      {/* Clauses */}
      <FormSection title={t('agreements.section.clauses')} defaultOpen={false}
        subtitle={canEdit && !clausesEditMode ? (
          <button type="button" onClick={openClausesEdit} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            <Edit3 className="h-3 w-3" />{t('agreements.action.edit')}
          </button>
        ) as unknown as string : undefined}>
        {clausesEditMode ? (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-primary" />
              {t('agreements.clauses.editHint')}
            </p>
            {Array.from({ length: 17 }).map((_, idx) => {
              const num = String(idx + 1);
              const value = clausesDraft[num] ?? '';
              return (
                <div key={num} className="rounded-md border border-border bg-muted/30 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-semibold">{t('agreements.clauses.clause')} {num}</label>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => openAi(num)} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                        <Bot className="h-3 w-3" />
                        {t('agreements.ai.askButton')}
                      </button>
                      {value && (
                        <button type="button" onClick={() => resetClause(num)} className="text-xs text-muted-foreground hover:text-foreground">
                          {t('agreements.clauses.reset')}
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea value={value} onChange={(e) => updateClause(num, e.target.value)} rows={4} className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                    placeholder={t('agreements.clauses.placeholder')} />
                </div>
              );
            })}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setClausesEditMode(false)} disabled={savingClauses}>{t('common.cancel')}</Button>
              <Button size="sm" onClick={saveClauses} disabled={savingClauses}>
                {savingClauses ? t('agreements.edit.saving') : t('agreements.edit.save')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {Object.keys(fv.clauses ?? {}).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('agreements.clauses.noneOverridden')}</p>
            ) : (
              Object.entries(fv.clauses ?? {}).map(([num, c]) => (
                <div key={num} className="rounded-md border border-amber-700/40 bg-amber-950/10 p-3">
                  <div className="text-xs font-semibold uppercase text-amber-300 mb-1">{t('agreements.clauses.clause')} {num} · {t('agreements.clauses.overridden')}</div>
                  <p className="whitespace-pre-wrap text-sm">{c.body}</p>
                </div>
              ))
            )}
          </div>
        )}
      </FormSection>

      {/* Versions */}
      <FormSection title={t('agreements.section.versions')} defaultOpen>
        <ul className="divide-y rounded-md border">
          {agreement.versions.map((v) => {
            const isCurrent = v.id === agreement.currentVersionId;
            return (
              <li key={v.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">
                    v{v.version} · {t(ORIGIN_LABEL_KEY(v.origin))}
                    {isCurrent && <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] text-primary">{t('agreements.version.current')}</span>}
                  </div>
                  {v.notes && <div className="text-xs text-muted-foreground whitespace-pre-wrap">{v.notes}</div>}
                </div>
                <div className="text-xs text-muted-foreground">{v.createdAt}</div>
              </li>
            );
          })}
          {agreement.versions.length === 0 && <li className="px-3 py-4 text-center text-xs text-muted-foreground">{t('agreements.versions.empty')}</li>}
        </ul>
      </FormSection>

      {/* Signatures */}
      <FormSection title={t('agreements.section.signatures')} defaultOpen>
        {agreement.signatures.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('agreements.signatures.empty')}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {agreement.signatures.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{s.signerName}{s.signerTitle ? ` · ${s.signerTitle}` : ''}</div>
                  <div className="text-xs text-muted-foreground">
                    {t(PARTY_LABEL_KEY(s.signerParty))} · {t(METHOD_LABEL_KEY(s.method))}
                    {s.signerEmail && ` · ${s.signerEmail}`}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">{s.signedAt}</div>
              </li>
            ))}
          </ul>
        )}
      </FormSection>

      {agreement.notes && (
        <FormSection title={t('agreements.section.notes')} defaultOpen={false}>
          <p className="whitespace-pre-wrap text-sm">{agreement.notes}</p>
        </FormSection>
      )}

      {/* ─── Signature modal ─── */}
      {sigOpen && (
        <Modal onClose={() => setSigOpen(false)} title={t('agreements.signature.modal.title')}>
          <form onSubmit={submitSignature} className="space-y-3">
            <FormGrid>
              <FormField label={t('agreements.signature.party.label')}>
                <select value={sigForm.signerParty} onChange={(e) => setSigForm({ ...sigForm, signerParty: e.target.value as SignerParty })} className="w-full rounded-md border bg-background px-3 py-2 text-sm">
                  {ALL_PARTIES.map((p) => <option key={p} value={p}>{t(PARTY_LABEL_KEY(p))}</option>)}
                </select>
              </FormField>
              <FormField label={t('agreements.signature.method.label')}>
                <select value={sigForm.method} onChange={(e) => setSigForm({ ...sigForm, method: e.target.value as SignatureMethod })} className="w-full rounded-md border bg-background px-3 py-2 text-sm">
                  {ALL_METHODS.map((m) => <option key={m} value={m}>{t(METHOD_LABEL_KEY(m))}</option>)}
                </select>
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('agreements.signature.field.name')} required>
                <Input value={sigForm.signerName} onChange={(e) => setSigForm({ ...sigForm, signerName: e.target.value })} required />
              </FormField>
              <FormField label={t('agreements.signature.field.title')}>
                <Input value={sigForm.signerTitle} onChange={(e) => setSigForm({ ...sigForm, signerTitle: e.target.value })} />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('agreements.signature.field.email')}>
                <Input type="email" value={sigForm.signerEmail} onChange={(e) => setSigForm({ ...sigForm, signerEmail: e.target.value })} />
              </FormField>
              <FormField label={t('agreements.signature.field.signedAt')}>
                <Input type="datetime-local" value={sigForm.signedAt} onChange={(e) => setSigForm({ ...sigForm, signedAt: e.target.value })} />
              </FormField>
            </FormGrid>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" onClick={() => setSigOpen(false)} disabled={savingSig}>{t('common.cancel')}</Button>
              <Button type="submit" size="sm" disabled={savingSig || !sigForm.signerName.trim()}>
                {savingSig ? t('agreements.edit.saving') : t('agreements.signature.submit')}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* ─── New version modal ─── */}
      {newVerOpen && (
        <Modal onClose={() => setNewVerOpen(false)} title={t('agreements.newVersion.title')}>
          <p className="text-sm text-muted-foreground">{t('agreements.newVersion.hint')}</p>
          <FormField label={t('agreements.newVersion.notesLabel')}>
            <textarea rows={3} value={newVerNotes} onChange={(e) => setNewVerNotes(e.target.value)} className="w-full rounded-md border bg-background px-3 py-2 text-sm" placeholder={t('agreements.newVersion.notesPlaceholder')} />
          </FormField>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={() => setNewVerOpen(false)} disabled={savingNewVer}>{t('common.cancel')}</Button>
            <Button type="button" size="sm" onClick={submitNewVersion} disabled={savingNewVer}>
              {savingNewVer ? t('agreements.edit.saving') : t('agreements.newVersion.submit')}
            </Button>
          </div>
        </Modal>
      )}

      {/* ─── Upload counterparty modal ─── */}
      {upCpOpen && (
        <Modal onClose={() => setUpCpOpen(false)} title={t('agreements.uploadCp.title')}>
          <p className="text-sm text-muted-foreground">{t('agreements.uploadCp.hint')}</p>
          <FormField label={t('agreements.uploadCp.fileLabel')} hint={t('agreements.uploadCp.fileHint')}>
            <label className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm cursor-pointer hover:bg-accent">
              <Paperclip className="h-4 w-4" />
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setUpCpFile(e.target.files?.[0] ?? null)}
                className="hidden"
              />
              <span className="flex-1">{upCpFile ? upCpFile.name : t('agreements.uploadCp.pickFile')}</span>
              {upCpFile && <span className="text-xs text-muted-foreground">{(upCpFile.size/1024).toFixed(0)} KB</span>}
            </label>
          </FormField>
          <FormField label={t('agreements.uploadCp.notesLabel')}>
            <textarea rows={4} value={upCpNotes} onChange={(e) => setUpCpNotes(e.target.value)} className="w-full rounded-md border bg-background px-3 py-2 text-sm" placeholder={t('agreements.uploadCp.notesPlaceholder')} />
          </FormField>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={() => setUpCpOpen(false)} disabled={savingUpCp}>{t('common.cancel')}</Button>
            <Button type="button" size="sm" onClick={submitUploadCp} disabled={savingUpCp}>
              {savingUpCp ? t('agreements.edit.saving') : t('agreements.uploadCp.submit')}
            </Button>
          </div>
        </Modal>
      )}

      {/* ─── AI clause assist modal ─── */}
      {aiOpen && (
        <Modal onClose={() => setAiOpen(false)} title={t('agreements.ai.title')} wide>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('agreements.ai.hint', { num: aiClauseNum })}</p>
            <FormField label={t('agreements.ai.instructionLabel')} hint={t('agreements.ai.instructionHint')}>
              <textarea
                rows={3}
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder={t('agreements.ai.instructionPlaceholder')}
              />
            </FormField>
            <div className="flex justify-end">
              <Button type="button" size="sm" onClick={runAi} disabled={aiLoading || !aiInstruction.trim()}>
                <Bot className="mr-1 h-3.5 w-3.5" />
                {aiLoading ? t('agreements.ai.thinking') : t('agreements.ai.runButton')}
              </Button>
            </div>
            {aiResult && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">
                  {t('agreements.ai.resultLabel')} · {aiModel}
                </div>
                <textarea
                  rows={12}
                  value={aiResult}
                  onChange={(e) => setAiResult(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setAiOpen(false)}>{t('common.cancel')}</Button>
                  <Button type="button" size="sm" onClick={applyAi}>
                    <Sparkles className="mr-1 h-3.5 w-3.5" />
                    {t('agreements.ai.applyButton')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ─── Diff modal ─── */}
      {diffOpen && (
        <Modal onClose={() => setDiffOpen(false)} title={t('agreements.diff.title')} wide>
          <DiffView
            versions={agreement.versions}
            fromId={diffFromId}
            toId={diffToId}
            onChangeFrom={setDiffFromId}
            onChangeTo={setDiffToId}
            t={t}
          />
        </Modal>
      )}
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────

function PartyBlock({ label, value }: { label: string; value?: PartyValue }) {
  if (!value) return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 rounded-md border bg-muted/30 px-3 py-2 text-sm">—</div>
    </div>
  );
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 space-y-1 rounded-md border bg-muted/30 px-3 py-2 text-sm">
        <div className="font-semibold">{value.name ?? '—'} ({value.short_name ?? '—'})</div>
        <div className="text-xs text-muted-foreground">{value.legal_form ?? '—'}</div>
        <div className="text-xs">{value.address ?? '—'}</div>
        <div className="pt-1 text-xs">
          <span className="font-medium">{value.representative ?? '—'}</span>
          {value.representative_title ? ` · ${value.representative_title}` : ''}
        </div>
        {value.governance_contact && (
          <div className="border-t pt-1 text-xs text-muted-foreground">
            Gov: {value.governance_contact.name ?? '—'}
            {value.governance_contact.title ? ` · ${value.governance_contact.title}` : ''}
            {value.governance_contact.email ? ` · ${value.governance_contact.email}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

function ReadField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">{value || '—'}</div>
    </div>
  );
}

function Modal({
  title, onClose, children, wide,
}: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 print:hidden">
      <div className={`max-h-[90vh] w-full ${wide ? 'max-w-5xl' : 'max-w-lg'} overflow-y-auto rounded-lg border bg-card p-6 shadow-xl`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DiffView({
  versions, fromId, toId, onChangeFrom, onChangeTo, t,
}: {
  versions: AgreementVersionSummary[];
  fromId: string; toId: string;
  onChangeFrom: (v: string) => void; onChangeTo: (v: string) => void;
  t: (k: string) => string;
}) {
  const from = versions.find((v) => v.id === fromId);
  const to = versions.find((v) => v.id === toId);
  const fromLines = JSON.stringify(from?.fieldValues ?? {}, null, 2).split('\n');
  const toLines = JSON.stringify(to?.fieldValues ?? {}, null, 2).split('\n');
  const max = Math.max(fromLines.length, toLines.length);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={fromId} onChange={(e) => onChangeFrom(e.target.value)} className="rounded-md border bg-background px-2 py-1 text-sm">
          {versions.map((v) => <option key={v.id} value={v.id}>v{v.version} — {v.origin}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">↔</span>
        <select value={toId} onChange={(e) => onChangeTo(e.target.value)} className="rounded-md border bg-background px-2 py-1 text-sm">
          {versions.map((v) => <option key={v.id} value={v.id}>v{v.version} — {v.origin}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto rounded-md border">
        <pre className="p-3 text-[11px] font-mono overflow-x-auto whitespace-pre">
          {Array.from({ length: max }).map((_, i) => {
            const a = fromLines[i] ?? '';
            const b = toLines[i] ?? '';
            const differ = a !== b;
            return <div key={i} className={differ ? 'bg-red-900/20' : ''}>{a || ' '}</div>;
          })}
        </pre>
        <pre className="p-3 text-[11px] font-mono overflow-x-auto whitespace-pre border-l">
          {Array.from({ length: max }).map((_, i) => {
            const a = fromLines[i] ?? '';
            const b = toLines[i] ?? '';
            const differ = a !== b;
            return <div key={i} className={differ ? 'bg-emerald-900/20' : ''}>{b || ' '}</div>;
          })}
        </pre>
      </div>
      <p className="text-xs text-muted-foreground">{t('agreements.diff.legend')}</p>
    </div>
  );
}

/**
 * Build a printable HTML document for export to Word (Word reads HTML
 * with a .doc extension natively). Kept self-contained so the .doc file
 * works outside the SPA.
 */
function renderAgreementHtml(
  agreement: AgreementDetail,
  version: AgreementVersionSummary | null,
  fv: FieldValuesShape,
): string {
  const partyRows = (fv.parties ?? []).map((p, i) => `
    <h3>${i === 0 ? 'PARTE A' : i === 1 ? 'PARTE B' : `PARTE ${i + 1}`}</h3>
    <p><strong>${escapeHtml(p.name ?? '')}</strong> (${escapeHtml(p.short_name ?? '')})<br/>
       ${escapeHtml(p.legal_form ?? '')}<br/>
       ${escapeHtml(p.address ?? '')}<br/>
       Representante: <strong>${escapeHtml(p.representative ?? '')}</strong>${p.representative_title ? ' · ' + escapeHtml(p.representative_title) : ''}</p>
  `).join('');
  const clausesHtml = Object.entries(fv.clauses ?? {})
    .map(([num, c]) => `<h2>Cláusula ${escapeHtml(num)}</h2><div>${escapeHtml(c.body ?? '').replace(/\n/g, '<br/>')}</div>`)
    .join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(agreement.title)}</title>
<style>body{font-family:Calibri,sans-serif;font-size:11pt;max-width:7in;margin:1in auto;color:#000}
h1{font-size:16pt;text-align:center}h2{font-size:13pt;color:#1e40af;margin-top:1em}h3{font-size:11pt;margin-top:0.5em}
.meta{color:#555;font-size:9pt;border-top:1px solid #ddd;padding-top:8pt;margin-top:24pt}
</style></head><body>
<h1>${escapeHtml(agreement.title)}</h1>
<p style="text-align:center;color:#555;">${escapeHtml(agreement.kind)} · ${escapeHtml(agreement.status)} · v${version?.version ?? '—'}</p>
${partyRows}
${clausesHtml}
<div class="meta">
  Generado desde Kairos · ${new Date().toLocaleString()}<br/>
  Contraparte: ${escapeHtml(agreement.counterpartyDisplayName)}<br/>
  Ley aplicable: ${escapeHtml(fv.governing_law ?? agreement.governingLaw ?? '—')}<br/>
  Jurisdicción: ${escapeHtml(fv.arbitration_seat ?? agreement.governingJurisdiction ?? '—')}
</div>
</body></html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}
